import { useState, useEffect } from 'react';
import api from '../api';
import { useLang } from '../context/LangContext';

const METERS = ['enkelvoudig', 'tweevoudig', 'digitaal'];

function ConnModal({ conn, onClose, onSave, t }) {
  const isNew = !conn?.id;
  const [form, setForm] = useState({
    label:       conn?.label       || '',
    ean_code:    conn?.ean_code    || '',
    energy_type: conn?.energy_type || 'elektriciteit',
    address:     conn?.address     || '',
    meter_type:  conn?.meter_type  || '',
    has_solar:   conn?.has_solar   ?? 0,
    supplier:    conn?.supplier    || 'Engie',
    notes:       conn?.notes       || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      if (isNew) await api.post('/api/connections', form);
      else       await api.put(`/api/connections/${conn.id}`, form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    } finally {
      setLoading(false);
    }
  }

  const isElec = form.energy_type === 'elektriciteit';

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{isNew ? t('conn_new') : t('edit')}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <form id="conn-form" onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label small fw-medium">{t('conn_label')} *</label>
                  <input className="form-control" value={form.label}
                    onChange={e => set('label', e.target.value)} required autoFocus
                    placeholder="bv. Woning – elektriciteit" />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label small fw-medium">{t('conn_type')} *</label>
                    <select className="form-select" value={form.energy_type}
                      onChange={e => set('energy_type', e.target.value)}>
                      <option value="elektriciteit">{t('type_elektriciteit')}</option>
                      <option value="gas">{t('type_gas')}</option>
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label small fw-medium">{t('conn_ean')}</label>
                    <input className="form-control font-mono" value={form.ean_code}
                      onChange={e => set('ean_code', e.target.value)} placeholder="54..." />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-medium">{t('conn_address')}</label>
                  <input className="form-control" value={form.address}
                    onChange={e => set('address', e.target.value)} />
                </div>
                <div className="row g-3 mb-3">
                  {isElec && (
                    <div className="col">
                      <label className="form-label small fw-medium">{t('conn_meter')}</label>
                      <select className="form-select" value={form.meter_type}
                        onChange={e => set('meter_type', e.target.value)}>
                        <option value="">—</option>
                        {METERS.map(m => <option key={m} value={m}>{t(`meter_${m}`)}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="col">
                    <label className="form-label small fw-medium">{t('conn_supplier')}</label>
                    <input className="form-control" value={form.supplier}
                      onChange={e => set('supplier', e.target.value)} />
                  </div>
                </div>
                {isElec && (
                  <div className="form-check mb-3">
                    <input className="form-check-input" type="checkbox" id="has_solar"
                      checked={!!form.has_solar} onChange={e => set('has_solar', e.target.checked ? 1 : 0)} />
                    <label className="form-check-label small" htmlFor="has_solar">{t('conn_solar')}</label>
                  </div>
                )}
                <div className="mb-1">
                  <label className="form-label small fw-medium">{t('conn_notes')}</label>
                  <textarea className="form-control" rows="2" value={form.notes}
                    onChange={e => set('notes', e.target.value)} />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
              <button type="submit" form="conn-form" className="btn btn-primary" disabled={loading}>
                {loading && <span className="spinner-border spinner-border-sm me-2" />}
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

export default function ConnectionsPage() {
  const { t } = useLang();
  const [conns, setConns]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);

  function load() {
    setLoading(true);
    api.get('/api/connections').then(r => setConns(r.data)).finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleDelete(c) {
    if (!window.confirm(t('confirm_delete'))) return;
    await api.delete(`/api/connections/${c.id}`);
    load();
  }

  return (
    <>
      <div className="page-header">
        <h2>{t('connections_title')}</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setModal('new')}>
          <i className="bi bi-plus me-1" />{t('conn_new')}
        </button>
      </div>

      <div className="page-body">
        <div className="card">
          {loading ? (
            <div className="card-body text-center text-muted py-4">
              <div className="spinner-border spinner-border-sm me-2" />{t('loading')}
            </div>
          ) : conns.length === 0 ? (
            <div className="card-body text-center text-muted py-5">{t('no_data')}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t('conn_label')}</th>
                    <th>{t('conn_type')}</th>
                    <th>{t('conn_ean')}</th>
                    <th className="text-end">{t('conn_invoices')}</th>
                    <th>{t('conn_last_period')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {conns.map(c => (
                    <tr key={c.id}>
                      <td className="fw-medium">
                        {c.label}
                        {!!c.has_solar && <i className="bi bi-sun ms-2 text-warning" title="Zonnepanelen" />}
                      </td>
                      <td><span className={`badge badge-${c.energy_type}`}>{t(`type_${c.energy_type}`)}</span></td>
                      <td className="text-muted font-mono" style={{ fontSize: 12 }}>{c.ean_code || '—'}</td>
                      <td className="text-end">{c.invoice_count}</td>
                      <td className="text-muted">{c.last_period_end ? c.last_period_end.slice(0, 10) : '—'}</td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => setModal(c)}>
                          {t('edit')}
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(c)}>
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <ConnModal
          conn={modal === 'new' ? null : modal}
          t={t}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}
